import { useEffect, useState, useRef } from 'react'
import { billingService } from '../services/billing'
import {
  adminService,
  type Tenant,
  type Wallet as WalletType,
  type WalletTransaction,
} from '../../identity/services/admin'
import type { BillingService, Subscription, PricingCatalog } from '../../../types'
import { useEscapeKey } from '../../../lib/useEscapeKey'
import { useWindowWidth } from '../../../lib/useWindowWidth'
import { ModalOverlay } from '../../../components/ModalOverlay'
import {
  CreditCard, RefreshCw, Plus, Play, Pause, XCircle,
  X, AlertCircle, Tag, Wallet, ChevronDown,
  ArrowUpRight, ArrowDownRight, Clock, Package,
} from 'lucide-react'

// ── Helpers ───────────────────────────────────────────────────────────────────

const fmtDate = (s: string) =>
  new Date(s).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })

const fmtDateTime = (s: string) =>
  new Date(s).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })

const fmt = (n: number) =>
  new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(n)

const STATUS_BADGE: Record<string, string> = {
  active:    'badge-success',
  suspended: 'badge-warning',
  cancelled: 'badge-danger',
  expired:   'badge-neutral',
}

const CYCLES = [{ value: 'monthly', label: 'Monthly' }, { value: 'annual', label: 'Annual' }]

// ── Top Up Modal ──────────────────────────────────────────────────────────────

function TopUpModal({ tenant, onClose, onDone }: {
  tenant: Tenant; onClose: () => void; onDone: () => void
}) {
  const [form, setForm] = useState({ amount: '', description: '', reference: '' })
  const [saving, setSaving] = useState(false)
  const [error, setError]   = useState('')

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    const amount = Number(form.amount)
    if (!amount || amount <= 0) { setError('Enter a positive amount'); return }
    setSaving(true); setError('')
    try {
      await adminService.topUpWallet(tenant.id, {
        amount,
        description: form.description || `Manual top-up for ${tenant.name}`,
        reference: form.reference || undefined,
      })
      onDone(); onClose()
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail
      setError(msg || 'Top-up failed')
    } finally { setSaving(false) }
  }

  return (
    <ModalOverlay onClose={onClose}>
      <div className="modal-panel" style={{ maxWidth: 400 }}>
        <div className="modal-header">
          <div>
            <h2 className="text-lg font-semibold" style={{ color: 'var(--text-primary)' }}>Top Up Wallet</h2>
            <p className="text-sm mt-0.5" style={{ color: 'var(--text-tertiary)' }}>{tenant.name}</p>
          </div>
          <button className="btn-icon" onClick={onClose}><X className="h-4 w-4" /></button>
        </div>
        <form onSubmit={handleSubmit} className="space-y-3">
          <div className="form-group">
            <label className="form-label">Amount (INR)</label>
            <input className="input" required type="number" min="1" step="0.01" placeholder="0.00"
              value={form.amount} onChange={e => setForm(f => ({ ...f, amount: e.target.value }))} />
          </div>
          <div className="form-group">
            <label className="form-label">Description</label>
            <input className="input" placeholder={`Manual top-up for ${tenant.name}`}
              value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} />
          </div>
          <div className="form-group">
            <label className="form-label">
              Reference <span style={{ color: 'var(--text-tertiary)', fontWeight: 400 }}>(optional)</span>
            </label>
            <input className="input" placeholder="e.g. TXN-20240601"
              value={form.reference} onChange={e => setForm(f => ({ ...f, reference: e.target.value }))} />
          </div>
          {error && <div className="alert-danger text-sm">{error}</div>}
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" className="btn-secondary" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn-primary" disabled={saving}>
              {saving ? 'Processing…' : 'Add Funds'}
            </button>
          </div>
        </form>
      </div>
    </ModalOverlay>
  )
}

// ── Transaction Drawer ────────────────────────────────────────────────────────

function TransactionDrawer({ tenant, onClose }: { tenant: Tenant; onClose: () => void }) {
  useEscapeKey(onClose)
  const [txns, setTxns] = useState<WalletTransaction[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    adminService.listTransactions(tenant.id).then(setTxns).finally(() => setLoading(false))
  }, [tenant.id])

  const TXN_ICON: Record<string, React.ReactNode> = {
    CREDIT: <ArrowUpRight  className="h-3.5 w-3.5" style={{ color: 'var(--success-text)' }} />,
    DEBIT:  <ArrowDownRight className="h-3.5 w-3.5" style={{ color: 'var(--danger-text)' }} />,
    REFUND: <ArrowUpRight  className="h-3.5 w-3.5" style={{ color: 'var(--info-text)' }} />,
  }

  return (
    <>
      <div className="fixed inset-0 bg-black/30 z-40 backdrop-blur-sm" onClick={onClose} />
      <div
        className="fixed inset-y-0 right-0 z-50 flex flex-col"
        style={{
          width: 420, background: 'var(--surface-card)',
          borderLeft: '1px solid var(--border-default)', boxShadow: 'var(--shadow-xl)',
        }}
      >
        <div className="flex items-start justify-between px-5 py-4 shrink-0"
          style={{ borderBottom: '1px solid var(--border-default)' }}>
          <div>
            <h3 className="font-semibold" style={{ color: 'var(--text-primary)' }}>Wallet Transactions</h3>
            <p className="text-sm mt-0.5" style={{ color: 'var(--text-tertiary)' }}>{tenant.name} · last 200</p>
          </div>
          <button className="btn-icon" onClick={onClose}><X className="h-4 w-4" /></button>
        </div>
        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="empty-state"><div className="spinner" /></div>
          ) : txns.length === 0 ? (
            <div className="empty-state">
              <Clock className="h-8 w-8 mb-2" style={{ color: 'var(--text-tertiary)' }} />
              <p>No transactions yet</p>
            </div>
          ) : (
            <div className="divide-y" style={{ borderColor: 'var(--border-default)' }}>
              {txns.map(t => (
                <div key={t.id} className="flex items-start gap-3 px-5 py-3.5">
                  <div className="mt-0.5 rounded-full p-1.5 shrink-0"
                    style={{ background: t.transaction_type === 'DEBIT' ? 'var(--danger-bg)' : 'var(--success-bg)' }}>
                    {TXN_ICON[t.transaction_type]}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate" title={t.description} style={{ color: 'var(--text-primary)' }}>
                      {t.description}
                    </p>
                    <div className="flex items-center gap-2 mt-0.5">
                      <span className="text-xs" style={{ color: 'var(--text-tertiary)' }}>{t.actor_name}</span>
                      <span style={{ color: 'var(--border-strong)' }}>·</span>
                      <span className="text-xs" style={{ color: 'var(--text-tertiary)' }}>{fmtDateTime(t.created_at)}</span>
                    </div>
                    {t.reference && (
                      <p className="text-xs font-mono mt-0.5 truncate" title={t.reference} style={{ color: 'var(--text-tertiary)' }}>
                        {t.reference}
                      </p>
                    )}
                  </div>
                  <div className="text-right shrink-0 ml-2">
                    <p className="text-sm font-semibold tabular-nums"
                      style={{ color: t.transaction_type === 'DEBIT' ? 'var(--danger-text)' : 'var(--success-text)' }}>
                      {t.transaction_type === 'DEBIT' ? '−' : '+'}
                      {fmt(t.amount)}
                    </p>
                    <p className="text-xs mt-0.5 tabular-nums" style={{ color: 'var(--text-tertiary)' }}>
                      bal {fmt(t.balance_after)}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </>
  )
}

// ── Assign Plan Modal ─────────────────────────────────────────────────────────

function AssignPlanModal({
  tenant, services, pricingCatalog, onClose, onDone,
}: {
  tenant: Tenant; services: BillingService[]; pricingCatalog: PricingCatalog | null
  onClose: () => void; onDone: () => void
}) {
  const coreService = services.find(s => s.service_type === 'CORE' && s.is_active)
  // Derive available tiers from the pricing catalog — no hardcoding needed.
  // Adding a new tier to pricing.json automatically appears here.
  const tiers = Object.keys(
    pricingCatalog?.services?.[coreService?.slug ?? '']?.tiers ?? {}
  ).filter(t => t !== 'addon')
  const [form, setForm] = useState({ pricing_tier: 'standard', billing_cycle: 'monthly' })
  const [saving, setSaving] = useState(false)
  const [error, setError]   = useState('')

  const estimatedCost = coreService
    ? pricingCatalog?.services?.[coreService.slug]?.tiers?.[form.pricing_tier]?.[form.billing_cycle as 'monthly' | 'annual']
    : null

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!coreService) { setError('Core service not configured — restart the backend'); return }
    setSaving(true); setError('')
    try {
      const sub = await billingService.subscribe({ tenant_id: tenant.id, service_id: coreService.id })
      await billingService.assignTier(sub.id, { pricing_tier: form.pricing_tier, billing_cycle: form.billing_cycle })
      onDone(); onClose()
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail
      setError(msg || 'Failed to assign plan')
    } finally { setSaving(false) }
  }

  return (
    <ModalOverlay onClose={onClose}>
      <div className="modal-panel" style={{ maxWidth: 380 }}>
        <div className="modal-header">
          <div>
            <h2 className="text-lg font-semibold" style={{ color: 'var(--text-primary)' }}>Assign Plan</h2>
            <p className="text-sm mt-0.5" style={{ color: 'var(--text-tertiary)' }}>
              {tenant.name} · {tenant.domain}
            </p>
          </div>
          <button className="btn-icon" onClick={onClose}><X className="h-4 w-4" /></button>
        </div>
        <form onSubmit={handleSubmit} className="space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="form-group">
              <label className="form-label">Plan</label>
              <select className="input" value={form.pricing_tier}
                onChange={e => setForm(f => ({ ...f, pricing_tier: e.target.value }))}>
                {tiers.map(t => (
                  <option key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Billing Cycle</label>
              <select className="input" value={form.billing_cycle}
                onChange={e => setForm(f => ({ ...f, billing_cycle: e.target.value }))}>
                {CYCLES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
              </select>
            </div>
          </div>

          {estimatedCost != null && (
            <div className="rounded-lg px-3 py-2.5 text-sm"
              style={{ background: 'var(--info-bg)', color: 'var(--info-text)' }}>
              Cost: <strong>{fmt(Number(estimatedCost))}</strong>/{form.billing_cycle === 'annual' ? 'yr' : 'mo'}
              {' '}— debited from wallet per billing cycle.
            </div>
          )}
          {error && <div className="alert-danger text-sm">{error}</div>}
          <div className="flex justify-end gap-2 pt-1">
            <button type="button" className="btn-secondary" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn-primary" disabled={saving || !coreService || tiers.length === 0}>
              {saving ? 'Assigning…' : 'Assign Plan'}
            </button>
          </div>
        </form>
      </div>
    </ModalOverlay>
  )
}

// ── Change Plan Modal ─────────────────────────────────────────────────────────

function ChangePlanModal({
  subscription, tenantName, pricingCatalog, coreServiceSlug, onClose, onDone,
}: {
  subscription: Subscription; tenantName: string
  pricingCatalog: PricingCatalog | null; coreServiceSlug: string
  onClose: () => void; onDone: () => void
}) {
  // Derive available tiers from the pricing catalog — adding a tier to pricing.json
  // automatically makes it selectable here with no frontend code change.
  const tiers = Object.keys(
    pricingCatalog?.services?.[coreServiceSlug]?.tiers ?? {}
  ).filter(t => t !== 'addon')
  const [form, setForm] = useState({
    pricing_tier:  subscription.pricing_tier  || 'standard',
    billing_cycle: subscription.billing_cycle?.toLowerCase() || 'monthly',
  })
  const [saving, setSaving] = useState(false)
  const [error, setError]   = useState('')

  const estimatedCost = pricingCatalog?.services?.[coreServiceSlug]
    ?.tiers?.[form.pricing_tier]?.[form.billing_cycle as 'monthly' | 'annual']

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setSaving(true); setError('')
    try {
      await billingService.assignTier(subscription.id, form)
      onDone(); onClose()
    } catch (err: unknown) {
      const detail = (err as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail
      const msg = typeof detail === 'string'
        ? detail
        : Array.isArray(detail)
          ? detail.map((d: { msg?: string }) => d.msg).join('; ')
          : 'Failed to update plan'
      setError(msg)
    } finally { setSaving(false) }
  }

  return (
    <ModalOverlay onClose={onClose}>
      <div className="modal-panel" style={{ maxWidth: 360 }}>
        <div className="modal-header">
          <div>
            <h2 className="text-lg font-semibold" style={{ color: 'var(--text-primary)' }}>Change Plan</h2>
            <p className="text-sm mt-0.5" style={{ color: 'var(--text-tertiary)' }}>{tenantName}</p>
          </div>
          <button className="btn-icon" onClick={onClose}><X className="h-4 w-4" /></button>
        </div>
        <form onSubmit={handleSubmit} className="space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="form-group">
              <label className="form-label">Plan</label>
              <select className="input" value={form.pricing_tier}
                onChange={e => setForm(f => ({ ...f, pricing_tier: e.target.value }))}>
                {tiers.map(t => (
                  <option key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Billing Cycle</label>
              <select className="input" value={form.billing_cycle}
                onChange={e => setForm(f => ({ ...f, billing_cycle: e.target.value }))}>
                {CYCLES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
              </select>
            </div>
          </div>

          {estimatedCost != null && (
            <div className="rounded-lg px-3 py-2.5 text-sm"
              style={{ background: 'var(--info-bg)', color: 'var(--info-text)' }}>
              New cost: <strong>{fmt(Number(estimatedCost))}</strong>/{form.billing_cycle === 'annual' ? 'yr' : 'mo'}
            </div>
          )}
          {error && <div className="alert-danger text-sm">{error}</div>}
          <div className="flex justify-end gap-2 pt-1">
            <button type="button" className="btn-secondary" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn-primary" disabled={saving || tiers.length === 0}>
              {saving ? 'Saving…' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </ModalOverlay>
  )
}

// ── Cancel confirmation ───────────────────────────────────────────────────────

function CancelConfirm({
  tenantName, onConfirm, onClose,
}: {
  tenantName: string; onConfirm: () => void; onClose: () => void
}) {
  return (
    <ModalOverlay onClose={onClose}>
      <div className="modal-panel" style={{ maxWidth: 400 }}>
        <div className="modal-header">
          <div className="flex items-center gap-2">
            <AlertCircle className="h-5 w-5" style={{ color: 'var(--danger-text)' }} />
            <h2 className="text-base font-semibold" style={{ color: 'var(--text-primary)' }}>Cancel Plan</h2>
          </div>
          <button className="btn-icon" onClick={onClose}><X className="h-4 w-4" /></button>
        </div>
        <p className="text-sm mb-6" style={{ color: 'var(--text-secondary)' }}>
          Cancel the plan for <strong>{tenantName}</strong>? They will lose access to the platform.
        </p>
        <div className="flex justify-end gap-3">
          <button className="btn-secondary" onClick={onClose}>Keep Active</button>
          <button className="btn-danger" onClick={onConfirm}>Yes, Cancel</button>
        </div>
      </div>
    </ModalOverlay>
  )
}

// ── Add Add-on Modal ──────────────────────────────────────────────────────────

function AddAddonModal({
  tenant, services, existingSubSlugs, pricingCatalog, onClose, onDone,
}: {
  tenant: Tenant; services: BillingService[]; existingSubSlugs: string[]
  pricingCatalog: PricingCatalog | null; onClose: () => void; onDone: () => void
}) {
  const available = services.filter(
    s => s.is_active && s.service_type === 'ADDON' && !existingSubSlugs.includes(s.slug)
  )
  const [form, setForm] = useState({
    service_id: available[0]?.id?.toString() ?? '',
    billing_cycle: 'monthly',
  })
  const [saving, setSaving] = useState(false)
  const [error, setError]   = useState('')

  const selectedService = available.find(s => s.id === Number(form.service_id))
  const addonPricing = selectedService
    ? pricingCatalog?.services?.[selectedService.slug]?.tiers?.['addon']
    : null
  const estimatedCost = addonPricing
    ? (form.billing_cycle === 'annual' ? addonPricing.annual : addonPricing.monthly)
    : null

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!form.service_id) { setError('No add-ons available'); return }
    setSaving(true); setError('')
    try {
      const sub = await billingService.subscribe({ tenant_id: tenant.id, service_id: Number(form.service_id) })
      await billingService.assignTier(sub.id, { pricing_tier: 'addon', billing_cycle: form.billing_cycle })
      onDone(); onClose()
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail
      setError(msg || 'Failed to add add-on')
    } finally { setSaving(false) }
  }

  if (available.length === 0) {
    return (
      <ModalOverlay onClose={onClose}>
        <div className="modal-panel" style={{ maxWidth: 360 }}>
          <div className="modal-header">
            <h2 className="text-lg font-semibold" style={{ color: 'var(--text-primary)' }}>Add Add-on</h2>
            <button className="btn-icon" onClick={onClose}><X className="h-4 w-4" /></button>
          </div>
          <p className="text-sm pb-2" style={{ color: 'var(--text-tertiary)' }}>
            All available add-ons are already active for {tenant.name}.
          </p>
        </div>
      </ModalOverlay>
    )
  }

  return (
    <ModalOverlay onClose={onClose}>
      <div className="modal-panel" style={{ maxWidth: 380 }}>
        <div className="modal-header">
          <div>
            <h2 className="text-lg font-semibold" style={{ color: 'var(--text-primary)' }}>Add Add-on</h2>
            <p className="text-sm mt-0.5" style={{ color: 'var(--text-tertiary)' }}>
              {tenant.name} · {tenant.domain}
            </p>
          </div>
          <button className="btn-icon" onClick={onClose}><X className="h-4 w-4" /></button>
        </div>
        <form onSubmit={handleSubmit} className="space-y-3">
          <div className="form-group">
            <label className="form-label">Add-on</label>
            <select className="input" value={form.service_id}
              onChange={e => setForm(f => ({ ...f, service_id: e.target.value }))}>
              {available.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          </div>
          <div className="form-group">
            <label className="form-label">Billing Cycle</label>
            <select className="input" value={form.billing_cycle}
              onChange={e => setForm(f => ({ ...f, billing_cycle: e.target.value }))}>
              {CYCLES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
            </select>
          </div>

          {estimatedCost != null && (
            <div className="rounded-lg px-3 py-2.5 text-sm"
              style={{ background: 'var(--info-bg)', color: 'var(--info-text)' }}>
              Cost: <strong>{fmt(Number(estimatedCost))}</strong>/{form.billing_cycle === 'annual' ? 'yr' : 'mo'}
              {' '}— debited from {tenant.name}'s wallet per billing cycle.
            </div>
          )}
          {error && <div className="alert-danger text-sm">{error}</div>}
          <div className="flex justify-end gap-2 pt-1">
            <button type="button" className="btn-secondary" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn-primary" disabled={saving}>
              {saving ? 'Adding…' : 'Add Add-on'}
            </button>
          </div>
        </form>
      </div>
    </ModalOverlay>
  )
}

// ── Add-ons Cell ─────────────────────────────────────────────────────────────
// Compact: shows "+ Add" (no addons) or "N add-ons ▾" badge (with addons).
// Click opens a fixed-position dropdown listing each addon with a remove button
// plus an "+ Add add-on" option at the bottom.

function AddonsCell({
  tenant, addons, canAdd,
  addonAnnualTotal, addonMonthlyTotal,
  onAdd, onRemove,
}: {
  tenant: Tenant
  addons: Subscription[]
  canAdd: boolean
  addonAnnualTotal: number
  addonMonthlyTotal: number
  onAdd: () => void
  onRemove: (sub: Subscription) => void
}) {
  const [open, setOpen]   = useState(false)
  const [pos,  setPos]    = useState({ top: 0, left: 0 })
  const btnRef  = useRef<HTMLButtonElement>(null)
  const menuRef = useRef<HTMLDivElement>(null)

  // Close on outside click or Escape
  useEffect(() => {
    if (!open) return
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node
      if (!btnRef.current?.contains(t) && !menuRef.current?.contains(t)) setOpen(false)
    }
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false) }
    document.addEventListener('mousedown', onDown)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onDown)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  const handleOpen = () => {
    if (btnRef.current) {
      const r = btnRef.current.getBoundingClientRect()
      // Prefer opening to the right; if near right edge, open to the left
      const spaceRight = window.innerWidth - r.left
      setPos({
        top: r.bottom + 4,
        left: spaceRight > 220 ? r.left : r.right - 220,
      })
    }
    setOpen(v => !v)
  }

  // No plan at all — no interaction possible
  if (!canAdd && addons.length === 0) {
    return <span style={{ color: 'var(--text-tertiary)', fontSize: 13 }}>—</span>
  }

  // No addons yet — show a simple "+ Add" link
  if (addons.length === 0) {
    return (
      <button
        onClick={onAdd}
        className="flex items-center gap-0.5 text-xs font-medium"
        style={{ color: 'var(--brand-primary)' }}
      >
        <Plus className="h-3 w-3" />
        Add
      </button>
    )
  }

  // Has addons — compact badge + cost summary + dropdown
  return (
    <>
      <div>
      <button
        ref={btnRef}
        onClick={handleOpen}
        className="inline-flex items-center gap-1 badge-neutral"
        style={{ fontSize: 11, padding: '3px 8px', cursor: 'pointer', userSelect: 'none' }}
      >
        <Package className="h-3 w-3" style={{ color: 'var(--text-tertiary)' }} />
        {addons.length} add-on{addons.length !== 1 ? 's' : ''}
        <ChevronDown
          className="h-3 w-3"
          style={{
            color: 'var(--text-tertiary)',
            transform: open ? 'rotate(180deg)' : 'none',
            transition: 'transform 0.15s',
          }}
        />
      </button>
      {(addonAnnualTotal > 0 || addonMonthlyTotal > 0) && (
        <div className="mt-0.5 space-y-0">
          {addonAnnualTotal > 0 && (
            <p className="text-xs tabular-nums" style={{ color: 'var(--text-tertiary)' }}>
              {fmt(addonAnnualTotal)}/yr
            </p>
          )}
          {addonMonthlyTotal > 0 && (
            <p className="text-xs tabular-nums" style={{ color: 'var(--text-tertiary)' }}>
              {fmt(addonMonthlyTotal)}/mo
            </p>
          )}
        </div>
      )}
      </div>

      {open && (
        <div
          ref={menuRef}
          style={{
            position: 'fixed', top: pos.top, left: pos.left, zIndex: 300,
            minWidth: 220,
            background: 'var(--surface-card)',
            border: '1px solid var(--border-default)',
            borderRadius: 8,
            boxShadow: 'var(--shadow-lg)',
            overflow: 'hidden',
          }}
        >
          {/* Header */}
          <div className="px-3 py-2" style={{ borderBottom: '1px solid var(--border-default)' }}>
            <p className="text-xs font-semibold uppercase tracking-wide" style={{ color: 'var(--text-tertiary)' }}>
              {tenant.name}
            </p>
          </div>

          {/* Addon list */}
          {addons.map(a => (
            <div
              key={a.id}
              className="flex items-center justify-between gap-2 px-3 py-2"
              style={{ borderBottom: '1px solid var(--border-subtle, var(--border-default))' }}
            >
              <div className="min-w-0">
                <p className="text-sm truncate" title={a.service_name} style={{ color: 'var(--text-primary)' }}>{a.service_name}</p>
                {a.price_per_cycle != null && (
                  <p className="text-xs tabular-nums" style={{ color: 'var(--text-tertiary)' }}>
                    {fmt(Number(a.price_per_cycle))}/{a.billing_cycle?.toLowerCase() === 'annual' ? 'yr' : 'mo'}
                  </p>
                )}
              </div>
              <button
                onClick={() => { onRemove(a); setOpen(false) }}
                className="btn-icon shrink-0"
                title={`Remove ${a.service_name}`}
                style={{ width: 24, height: 24, minWidth: 24 }}
              >
                <X className="h-3.5 w-3.5" style={{ color: 'var(--danger-text)' }} />
              </button>
            </div>
          ))}

          {/* Add more */}
          {canAdd && (
            <button
              onClick={() => { onAdd(); setOpen(false) }}
              className="w-full flex items-center gap-2 px-3 py-2.5 text-sm"
              style={{ color: 'var(--brand-primary)', background: 'transparent' }}
              onMouseEnter={e => (e.currentTarget.style.background = 'var(--surface-hover)')}
              onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
            >
              <Plus className="h-3.5 w-3.5" />
              Add add-on
            </button>
          )}
        </div>
      )}
    </>
  )
}

// ── Remove Add-on Confirmation ────────────────────────────────────────────────

function RemoveAddonConfirm({
  addonName, tenantName, onConfirm, onClose,
}: {
  addonName: string; tenantName: string; onConfirm: () => void; onClose: () => void
}) {
  return (
    <ModalOverlay onClose={onClose}>
      <div className="modal-panel" style={{ maxWidth: 420 }}>
        <div className="modal-header">
          <div className="flex items-center gap-2">
            <AlertCircle className="h-5 w-5" style={{ color: 'var(--warning-text)' }} />
            <h2 className="text-base font-semibold" style={{ color: 'var(--text-primary)' }}>Remove Add-on</h2>
          </div>
          <button className="btn-icon" onClick={onClose}><X className="h-4 w-4" /></button>
        </div>
        <p className="text-sm mb-3" style={{ color: 'var(--text-secondary)' }}>
          Remove <strong>{addonName}</strong> from <strong>{tenantName}</strong>?
        </p>
        <div className="rounded-lg px-3 py-2.5 mb-5 text-sm"
          style={{ background: 'var(--warning-bg)', color: 'var(--warning-text)' }}>
          Billing stops from the next cycle. The add-on remains accessible until the current period ends.
        </div>
        <div className="flex justify-end gap-3">
          <button className="btn-secondary" onClick={onClose}>Keep</button>
          <button className="btn-danger" onClick={onConfirm}>Remove Add-on</button>
        </div>
      </div>
    </ModalOverlay>
  )
}

// ── Main Page ─────────────────────────────────────────────────────────────────

type BillingTab = 'plans' | 'wallets'

export default function BillingPage() {
  const [tab, setTab] = useState<BillingTab>('plans')

  // Data
  const [tenants, setTenants]             = useState<Tenant[]>([])
  const [subscriptions, setSubscriptions] = useState<Subscription[]>([])
  const [services, setServices]           = useState<BillingService[]>([])
  const [wallets, setWallets]             = useState<WalletType[]>([])
  const [pricingCatalog, setPricingCatalog] = useState<PricingCatalog | null>(null)

  // UI state
  const [loading, setLoading]             = useState(true)
  const [loadError, setLoadError]         = useState('')
  const [actionLoading, setActionLoading] = useState<number | null>(null)

  // Plans tab modals
  const [assignTenant, setAssignTenant]   = useState<Tenant | null>(null)
  const [changePlanFor, setChangePlanFor] = useState<{ sub: Subscription; tenant: Tenant } | null>(null)
  const [cancelFor, setCancelFor]         = useState<{ sub: Subscription; tenant: Tenant } | null>(null)
  const [addAddonTenant, setAddAddonTenant] = useState<Tenant | null>(null)
  const [removeAddon, setRemoveAddon]     = useState<{ sub: Subscription; tenant: Tenant } | null>(null)

  // Wallets tab modals
  const [topUpTenant, setTopUpTenant]     = useState<Tenant | null>(null)
  const [txnTenant, setTxnTenant]         = useState<Tenant | null>(null)

  const load = async () => {
    setLoading(true); setLoadError('')
    try {
      const [t, subs, svcs, walletList, pricing] = await Promise.all([
        adminService.listTenants(),
        billingService.listSubscriptions(),
        billingService.listServices(),
        adminService.listWallets(),
        billingService.getPricing().catch(() => null),   // non-fatal
      ])
      setTenants(t)
      setSubscriptions(subs)
      setServices(svcs)
      setWallets(walletList)
      if (pricing) setPricingCatalog(pricing)
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail
      setLoadError(msg || 'Failed to load billing data — check the backend logs')
    } finally { setLoading(false) }
  }

  useEffect(() => { load() }, [])

  const handleLifecycle = async (subId: number, action: 'suspend' | 'reactivate' | 'cancel') => {
    setActionLoading(subId)
    try {
      if (action === 'suspend')    await billingService.suspendSubscription(subId)
      if (action === 'reactivate') await billingService.reactivateSubscription(subId)
      if (action === 'cancel')     await billingService.cancelSubscription(subId)
      await load()
    } finally { setActionLoading(null) }
  }

  const handleRemoveAddon = async (subId: number) => {
    try {
      await billingService.cancelSubscription(subId)
      await load()
    } catch {
      // error silently — table will refresh either way
    }
  }

  // ── Derived data ────────────────────────────────────────────────────────────

  const isMobile = useWindowWidth() < 768
  const [expandedPlanId, setExpandedPlanId] = useState<number | null>(null)
  const [expandedWalletId, setExpandedWalletId] = useState<number | null>(null)

  const subsByTenant = subscriptions.reduce<Record<number, Subscription[]>>((acc, s) => {
    ;(acc[s.tenant_id] ||= []).push(s)
    return acc
  }, {})

  const tenantById = Object.fromEntries(tenants.map(t => [t.id, t]))

  const activeSubsCount = subscriptions.filter(
    s => s.status?.toLowerCase() === 'active' && s.service_type === 'CORE'
  ).length

  const coreService = services.find(s => s.service_type === 'CORE' && s.is_active)

  const TABS: { key: BillingTab; label: string; icon: React.ElementType }[] = [
    { key: 'plans',   label: 'Plans',   icon: CreditCard },
    { key: 'wallets', label: 'Wallets', icon: Wallet },
  ]

  return (
    <div className="space-y-6">

      {/* ── Header ────────────────────────────────────────────────────────── */}
      <div className="section-header">
        <div>
          <h1 className="page-title flex items-center gap-2">
            <CreditCard className="h-6 w-6" style={{ color: 'var(--brand-primary)' }} />
            Billing
            <button onClick={load} disabled={loading} className="btn-icon" title="Refresh" aria-label="Refresh" style={{ marginLeft: 'auto' }}>
              <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            </button>
          </h1>
          <p className="page-subtitle">
            {loading
              ? 'Loading…'
              : `${activeSubsCount} active plan${activeSubsCount !== 1 ? 's' : ''} across ${tenants.filter(t => t.is_active).length} tenant${tenants.length !== 1 ? 's' : ''}`
            }
          </p>
        </div>
      </div>

      {loadError && <div className="alert-danger text-sm">{loadError}</div>}

      {/* ── Tabs ──────────────────────────────────────────────────────────── */}
      <div className="flex gap-1" style={{ borderBottom: '1px solid var(--border-default)' }}>
        {TABS.map(({ key, label, icon: Icon }) => (
          <button
            key={key}
            onClick={() => setTab(key)}
            className="flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors"
            style={{
              borderBottomColor: tab === key ? 'var(--brand-primary)' : 'transparent',
              color: tab === key ? 'var(--brand-primary)' : 'var(--text-tertiary)',
            }}
          >
            <Icon className="h-4 w-4" />
            {label}
          </button>
        ))}
      </div>

      {/* ════════════════════════════════════════════════════════════════════
          PLANS TAB
          ════════════════════════════════════════════════════════════════ */}
      {tab === 'plans' && isMobile && !loading && tenants.length > 0 && (
        <div className="flex flex-col gap-2">
          {tenants.map(tenant => {
            const subs    = subsByTenant[tenant.id] ?? []
            const coreSub = subs.find(s => s.service_type === 'CORE')
            const coreStatus = coreSub?.status?.toLowerCase()
            const addons = subs.filter(s => s.service_type === 'ADDON' && s.status?.toLowerCase() === 'active')
            const expanded = expandedPlanId === tenant.id
            return (
              <div key={tenant.id} className="card" style={{ padding: 0, overflow: 'hidden' }}>
                <div
                  className="flex items-center gap-2.5 px-3 py-3 cursor-pointer"
                  onClick={() => setExpandedPlanId(expanded ? null : tenant.id)}
                >
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold truncate" style={{ color: 'var(--text-primary)' }}>{tenant.name}</p>
                    <div className="flex items-center gap-1.5 mt-0.5">
                      {coreSub?.pricing_tier
                        ? <span className="badge-info capitalize" style={{ fontSize: 10 }}>{coreSub.pricing_tier}</span>
                        : <span className="text-xs" style={{ color: 'var(--text-tertiary)' }}>No plan</span>
                      }
                      {coreStatus && (
                        <span className={`${STATUS_BADGE[coreStatus] || 'badge-neutral'} capitalize`} style={{ fontSize: 10 }}>
                          {coreStatus}
                        </span>
                      )}
                    </div>
                  </div>
                  {addons.length > 0 && (
                    <span className="text-xs shrink-0" style={{ color: 'var(--text-tertiary)' }}>+{addons.length} add-on{addons.length > 1 ? 's' : ''}</span>
                  )}
                  <ChevronDown
                    className="h-4 w-4 shrink-0 transition-transform"
                    style={{ transform: expanded ? 'rotate(180deg)' : 'none', color: 'var(--text-tertiary)' }}
                  />
                </div>
                {expanded && (
                  <div style={{ borderTop: '1px solid var(--border-default)', padding: '10px 12px', background: 'var(--surface-subtle)' }}>
                    {coreSub?.next_billing_at && (
                      <div className="flex justify-between text-xs py-1">
                        <span style={{ color: 'var(--text-tertiary)' }}>Next renewal</span>
                        <span style={{ color: 'var(--text-primary)', fontWeight: 500 }}>{fmtDate(coreSub.next_billing_at)}</span>
                      </div>
                    )}
                    {coreSub?.billing_cycle && (
                      <div className="flex justify-between text-xs py-1">
                        <span style={{ color: 'var(--text-tertiary)' }}>Cycle</span>
                        <span style={{ color: 'var(--text-primary)', fontWeight: 500 }}>
                          {coreSub.billing_cycle.charAt(0).toUpperCase() + coreSub.billing_cycle.slice(1).toLowerCase()}
                          {coreSub.price_per_cycle != null && ` · ${fmt(Number(coreSub.price_per_cycle))}`}
                        </span>
                      </div>
                    )}
                    {addons.length > 0 && (
                      <div className="text-xs py-1">
                        <span style={{ color: 'var(--text-tertiary)' }}>Add-ons</span>
                        <div className="mt-1 flex flex-col gap-0.5">
                          {addons.map(a => (
                            <div key={a.id} className="flex justify-between">
                              <span style={{ color: 'var(--text-primary)', fontWeight: 500 }}>
                                {a.service_name ?? (a.pricing_tier ?? a.service_type).replace(/_/g, ' ')}
                              </span>
                              {a.price_per_cycle != null && (
                                <span style={{ color: 'var(--text-secondary)' }}>
                                  {fmt(Number(a.price_per_cycle))}/{a.billing_cycle === 'annual' ? 'yr' : 'mo'}
                                </span>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    <div className="flex gap-2 mt-3">
                      {!coreSub ? (
                        <button
                          className="flex-1 text-xs py-2 rounded-md"
                          style={{ background: 'var(--brand-primary)', color: 'white' }}
                          onClick={e => { e.stopPropagation(); setAssignTenant(tenant) }}
                        >
                          Assign Plan
                        </button>
                      ) : (coreStatus === 'active' || coreStatus === 'suspended') && (
                        <button
                          className="flex-1 text-xs py-2 rounded-md border"
                          style={{ borderColor: 'var(--brand-primary)', color: 'var(--brand-primary)', background: 'transparent' }}
                          onClick={e => { e.stopPropagation(); setChangePlanFor({ sub: coreSub, tenant }) }}
                        >
                          Change Plan
                        </button>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )
          })}
        </div>
      )}

      {tab === 'plans' && (isMobile ? (loading ? <div className="flex justify-center py-10"><div className="spinner" /></div> : tenants.length === 0 ? <div className="empty-state"><p style={{ color: 'var(--text-tertiary)' }}>No tenants yet</p></div> : null) : (
        <div className="table-card">
          <div className="table-card-header">
            <h2 className="font-semibold text-sm" style={{ color: 'var(--text-primary)' }}>
              Tenant Plans
            </h2>
          </div>
          <div className="overflow-x-auto">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Tenant</th>
                  <th>Plan</th>
                  <th>Status</th>
                  <th>Next Renewal</th>
                  <th>Add-ons</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={6} className="py-12 text-center">
                      <div className="spinner mx-auto" />
                    </td>
                  </tr>
                ) : tenants.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="py-12 text-center" style={{ color: 'var(--text-tertiary)' }}>
                      No tenants yet — onboard a dealer first.
                    </td>
                  </tr>
                ) : (
                  tenants.map(tenant => {
                    const subs    = subsByTenant[tenant.id] ?? []
                    const coreSub = subs.find(s => s.service_type === 'CORE')

                    // ── Normalise status to lowercase for all comparisons ──
                    const coreStatus = coreSub?.status?.toLowerCase()

                    // ── Only show ACTIVE add-ons (backend returns uppercase status) ──
                    const addons = subs.filter(
                      s => s.service_type === 'ADDON' && s.status?.toLowerCase() === 'active'
                    )

                    // ── Billing cycle label ─────────────────────────────────
                    const cycleLabel = coreSub?.billing_cycle
                      ? coreSub.billing_cycle.charAt(0).toUpperCase() +
                        coreSub.billing_cycle.slice(1).toLowerCase()
                      : null
                    // ── Core plan price (shown in Plan column) ──────────────
                    // Add-on prices are shown separately in the Add-ons column.
                    // price_per_cycle is Decimal from Python → comes as string, use Number()
                    // Core price split by cycle (annual / monthly)
                    const annualTotal = coreSub?.billing_cycle?.toUpperCase() === 'ANNUAL' && coreSub.price_per_cycle != null
                      ? Number(coreSub.price_per_cycle) : 0
                    const monthlyTotal = coreSub?.billing_cycle?.toUpperCase() === 'MONTHLY' && coreSub?.price_per_cycle != null
                      ? Number(coreSub.price_per_cycle) : 0

                    // ── Add-on price breakdown (shown in Add-ons column) ─────
                    const addonAnnualTotal = addons
                      .filter(a => a.billing_cycle?.toUpperCase() === 'ANNUAL' && a.price_per_cycle != null)
                      .reduce((sum, a) => sum + Number(a.price_per_cycle), 0)
                    const addonMonthlyTotal = addons
                      .filter(a => a.billing_cycle?.toUpperCase() === 'MONTHLY' && a.price_per_cycle != null)
                      .reduce((sum, a) => sum + Number(a.price_per_cycle), 0)

                    return (
                      <tr key={tenant.id}>
                        {/* Tenant */}
                        <td>
                          <p className="text-primary-data">{tenant.name}</p>
                          <p className="font-mono text-xs mt-0.5" style={{ color: 'var(--text-tertiary)' }}>
                            {tenant.domain}
                          </p>
                        </td>

                        {/* Plan — always shows actual tier name; add-on costs are in the Add-ons column */}
                        <td>
                          {coreSub?.pricing_tier ? (
                            <div>
                              <div className="flex items-center gap-1.5">
                                <span className="badge-info capitalize">
                                  {coreSub.pricing_tier}
                                </span>
                                {cycleLabel && (
                                  <span style={{ color: 'var(--text-tertiary)', fontSize: 11 }}>{cycleLabel}</span>
                                )}
                              </div>
                              {(annualTotal > 0 || monthlyTotal > 0) && (
                                <div className="mt-0.5 space-y-0.5">
                                  {annualTotal > 0 && (
                                    <p className="text-xs tabular-nums" style={{ color: 'var(--text-tertiary)' }}>
                                      {fmt(annualTotal)}/yr
                                    </p>
                                  )}
                                  {monthlyTotal > 0 && (
                                    <p className="text-xs tabular-nums" style={{ color: 'var(--text-tertiary)' }}>
                                      {fmt(monthlyTotal)}/mo
                                    </p>
                                  )}
                                </div>
                              )}
                            </div>
                          ) : coreSub ? (
                            <span style={{ color: 'var(--text-tertiary)', fontSize: 13 }}>Not set</span>
                          ) : (
                            <span style={{ color: 'var(--text-tertiary)', fontSize: 13 }}>No plan</span>
                          )}
                        </td>

                        {/* Status */}
                        <td>
                          {coreSub ? (
                            <span
                              className={STATUS_BADGE[coreStatus!] || 'badge-neutral'}
                              style={{ textTransform: 'capitalize' }}
                            >
                              {coreStatus}
                            </span>
                          ) : (
                            <span style={{ color: 'var(--text-tertiary)', fontSize: 13 }}>—</span>
                          )}
                        </td>

                        {/* Next Renewal */}
                        <td style={{ color: 'var(--text-tertiary)', fontSize: 13 }}>
                          {coreSub?.next_billing_at ? fmtDate(coreSub.next_billing_at) : '—'}
                        </td>

                        {/* Add-ons — compact cell */}
                        <td>
                          <AddonsCell
                            tenant={tenant}
                            addons={addons}
                            canAdd={!!coreSub && coreStatus === 'active'}
                            addonAnnualTotal={addonAnnualTotal}
                            addonMonthlyTotal={addonMonthlyTotal}
                            onAdd={() => setAddAddonTenant(tenant)}
                            onRemove={sub => setRemoveAddon({ sub, tenant })}
                          />
                        </td>

                        {/* Actions */}
                        <td>
                          {!coreSub ? (
                            <button
                              className="btn-secondary"
                              style={{ height: 28, fontSize: 12 }}
                              onClick={() => setAssignTenant(tenant)}
                            >
                              <Plus className="h-3 w-3" />
                              Assign Plan
                            </button>
                          ) : (
                            <div className="flex items-center gap-1">
                              {(coreStatus === 'active' || coreStatus === 'suspended') && (
                                <button
                                  className="btn-icon"
                                  title={coreSub.pricing_tier ? 'Change plan' : 'Set plan tier'}
                                  onClick={() => setChangePlanFor({ sub: coreSub, tenant })}
                                >
                                  <Tag className="h-4 w-4" style={{ color: 'var(--brand-primary)' }} />
                                </button>
                              )}
                              {coreStatus === 'active' && (
                                <button
                                  className="btn-icon"
                                  title="Suspend"
                                  disabled={actionLoading === coreSub.id}
                                  onClick={() => handleLifecycle(coreSub.id, 'suspend')}
                                >
                                  {actionLoading === coreSub.id
                                    ? <div className="spinner" style={{ width: 16, height: 16 }} />
                                    : <Pause className="h-4 w-4" style={{ color: 'var(--warning-text)' }} />
                                  }
                                </button>
                              )}
                              {coreStatus === 'suspended' && (
                                <button
                                  className="btn-icon"
                                  title="Reactivate"
                                  disabled={actionLoading === coreSub.id}
                                  onClick={() => handleLifecycle(coreSub.id, 'reactivate')}
                                >
                                  {actionLoading === coreSub.id
                                    ? <div className="spinner" style={{ width: 16, height: 16 }} />
                                    : <Play className="h-4 w-4" style={{ color: 'var(--success-text)' }} />
                                  }
                                </button>
                              )}
                              {(coreStatus === 'active' || coreStatus === 'suspended') && (
                                <button
                                  className="btn-icon"
                                  title="Cancel plan"
                                  disabled={actionLoading === coreSub.id}
                                  onClick={() => setCancelFor({ sub: coreSub, tenant })}
                                >
                                  <XCircle className="h-4 w-4" style={{ color: 'var(--danger-text)' }} />
                                </button>
                              )}
                            </div>
                          )}
                        </td>
                      </tr>
                    )
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      ))}

      {/* ════════════════════════════════════════════════════════════════════
          WALLETS TAB
          ════════════════════════════════════════════════════════════════ */}
      {tab === 'wallets' && isMobile && !loading && wallets.length > 0 && (
        <div className="flex flex-col gap-2">
          {wallets.slice().sort((a, b) => Number(a.balance) - Number(b.balance)).map(w => {
            const tenant = tenantById[w.tenant_id]
            const balance = Number(w.balance)
            const isLow = balance < 500
            const activeSubs = (subsByTenant[w.tenant_id] ?? []).filter(s => s.status?.toLowerCase() === 'active' && s.price_per_cycle != null)
            const monthlyBurn = activeSubs.filter(s => s.billing_cycle?.toUpperCase() === 'MONTHLY').reduce((sum, s) => sum + Number(s.price_per_cycle ?? 0), 0)
            const annualBurn = activeSubs.filter(s => s.billing_cycle?.toUpperCase() === 'ANNUAL').reduce((sum, s) => sum + Number(s.price_per_cycle ?? 0), 0)
            const monthlyEquivalent = annualBurn / 12 + monthlyBurn
            const runwayMonths = monthlyEquivalent > 0 ? Math.floor(balance / monthlyEquivalent) : null
            const expanded = expandedWalletId === w.id
            return (
              <div key={w.id} className="card" style={{ padding: 0, overflow: 'hidden' }}>
                <div
                  className="flex items-center gap-2.5 px-3 py-3 cursor-pointer"
                  onClick={() => setExpandedWalletId(expanded ? null : w.id)}
                >
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold truncate" style={{ color: 'var(--text-primary)' }}>
                      {tenant?.name ?? `Tenant #${w.tenant_id}`}
                    </p>
                    <span className="text-sm font-bold tabular-nums" style={{ color: isLow ? 'var(--danger-text)' : 'var(--success-text)' }}>
                      {fmt(balance)}
                      {isLow && <span className="ml-1 badge-danger" style={{ fontSize: 9, padding: '1px 4px' }}>LOW</span>}
                    </span>
                  </div>
                  {runwayMonths != null && (
                    <span className="text-xs shrink-0" style={{ color: runwayMonths < 2 ? 'var(--danger-text)' : 'var(--text-tertiary)' }}>
                      {runwayMonths}mo runway
                    </span>
                  )}
                  <ChevronDown
                    className="h-4 w-4 shrink-0 transition-transform"
                    style={{ transform: expanded ? 'rotate(180deg)' : 'none', color: 'var(--text-tertiary)' }}
                  />
                </div>
                {expanded && (
                  <div style={{ borderTop: '1px solid var(--border-default)', padding: '10px 12px', background: 'var(--surface-subtle)' }}>
                    {(annualBurn > 0 || monthlyBurn > 0) && (
                      <div className="flex justify-between text-xs py-1">
                        <span style={{ color: 'var(--text-tertiary)' }}>Burn rate</span>
                        <span style={{ color: 'var(--text-primary)', fontWeight: 500 }}>
                          {annualBurn > 0 && `${fmt(annualBurn)}/yr`}
                          {annualBurn > 0 && monthlyBurn > 0 && ' + '}
                          {monthlyBurn > 0 && `${fmt(monthlyBurn)}/mo`}
                        </span>
                      </div>
                    )}
                    <div className="flex justify-between text-xs py-1">
                      <span style={{ color: 'var(--text-tertiary)' }}>Status</span>
                      <span className={w.is_active ? 'badge-success' : 'badge-neutral'} style={{ fontSize: 10 }}>
                        {w.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </div>
                    <div className="flex justify-between text-xs py-1">
                      <span style={{ color: 'var(--text-tertiary)' }}>Last updated</span>
                      <span style={{ color: 'var(--text-primary)', fontWeight: 500 }}>{fmtDate(w.updated_at)}</span>
                    </div>
                    {tenant && (
                      <div className="flex gap-2 mt-3">
                        <button
                          className="flex-1 text-xs py-2 rounded-md"
                          style={{ background: 'var(--brand-primary)', color: 'white' }}
                          onClick={e => { e.stopPropagation(); setTopUpTenant(tenant) }}
                        >
                          Top Up
                        </button>
                        <button
                          className="flex-1 text-xs py-2 rounded-md border"
                          style={{ borderColor: 'var(--border-default)', color: 'var(--text-secondary)', background: 'transparent' }}
                          onClick={e => { e.stopPropagation(); setTxnTenant(tenant) }}
                        >
                          History
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )
          })}
        </div>
      )}

      {tab === 'wallets' && (isMobile ? (loading ? <div className="flex justify-center py-10"><div className="spinner" /></div> : wallets.length === 0 ? <div className="empty-state"><p style={{ color: 'var(--text-tertiary)' }}>No wallets found</p></div> : null) : (
        <div className="table-card">
          <div className="table-card-header">
            <h2 className="font-semibold text-sm" style={{ color: 'var(--text-primary)' }}>
              Tenant Wallets
            </h2>
            {/* Total funds summary */}
            {!loading && wallets.length > 0 && (
              <span className="text-sm" style={{ color: 'var(--text-tertiary)' }}>
                Total funds:{' '}
                <strong style={{ color: 'var(--text-primary)' }}>
                  {fmt(wallets.reduce((sum, w) => sum + Number(w.balance), 0))}
                </strong>
              </span>
            )}
          </div>
          <div className="overflow-x-auto">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Tenant</th>
                  <th>Balance</th>
                  <th>Burn Rate</th>
                  <th>Runway</th>
                  <th>Status</th>
                  <th>Last Updated</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={7} className="py-12 text-center">
                      <div className="spinner mx-auto" />
                    </td>
                  </tr>
                ) : wallets.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="py-12 text-center" style={{ color: 'var(--text-tertiary)' }}>
                      No wallets found. Onboard a dealer to create one.
                    </td>
                  </tr>
                ) : (
                  wallets
                    .slice()
                    .sort((a, b) => Number(a.balance) - Number(b.balance))  // lowest balance first
                    .map(w => {
                      const tenant  = tenantById[w.tenant_id]
                      const balance = Number(w.balance)
                      const isLow   = balance < 500

                      // Price breakdown by cycle — never mix units.
                      // price_per_cycle is Python Decimal → JSON string; use Number() to parse.
                      const activeSubs = (subsByTenant[w.tenant_id] ?? []).filter(
                        s => s.status?.toLowerCase() === 'active' && s.price_per_cycle != null
                      )
                      const walletAnnualTotal = activeSubs
                        .filter(s => s.billing_cycle?.toUpperCase() === 'ANNUAL')
                        .reduce((sum, s) => sum + Number(s.price_per_cycle ?? 0), 0)
                      const walletMonthlyTotal = activeSubs
                        .filter(s => s.billing_cycle?.toUpperCase() === 'MONTHLY')
                        .reduce((sum, s) => sum + Number(s.price_per_cycle ?? 0), 0)

                      // Normalise to monthly equivalent for runway calculation only
                      const monthlyEquivalent =
                        walletAnnualTotal / 12 + walletMonthlyTotal

                      // Runway in months before wallet runs dry
                      const runwayMonths = monthlyEquivalent > 0
                        ? Math.floor(balance / monthlyEquivalent)
                        : null

                      return (
                        <tr key={w.id}>
                          {/* Tenant */}
                          <td>
                            <p className="text-primary-data">
                              {tenant?.name ?? `Tenant #${w.tenant_id}`}
                            </p>
                            <p className="font-mono text-xs mt-0.5" style={{ color: 'var(--text-tertiary)' }}>
                              {tenant?.domain ?? '—'}
                            </p>
                          </td>

                          {/* Balance */}
                          <td>
                            <span
                              className="font-semibold tabular-nums"
                              style={{ color: isLow ? 'var(--danger-text)' : 'var(--success-text)' }}
                            >
                              {fmt(balance)}
                            </span>
                            {isLow && (
                              <span className="ml-2 badge-danger" style={{ fontSize: 10, padding: '2px 6px' }}>
                                Low
                              </span>
                            )}
                          </td>

                          {/* Burn Rate */}
                          <td>
                            {(walletAnnualTotal > 0 || walletMonthlyTotal > 0) ? (
                              <div className="space-y-0.5">
                                {walletAnnualTotal > 0 && (
                                  <p className="tabular-nums text-sm font-medium" style={{ color: 'var(--text-primary)' }}>
                                    {fmt(walletAnnualTotal)}/yr
                                  </p>
                                )}
                                {walletMonthlyTotal > 0 && (
                                  <p className="tabular-nums text-sm font-medium" style={{ color: 'var(--text-primary)' }}>
                                    {fmt(walletMonthlyTotal)}/mo
                                  </p>
                                )}
                                <p className="text-xs" style={{ color: 'var(--text-tertiary)' }}>
                                  {activeSubs.length} sub{activeSubs.length !== 1 ? 's' : ''}
                                </p>
                              </div>
                            ) : (
                              <span style={{ color: 'var(--text-tertiary)', fontSize: 13 }}>No active plan</span>
                            )}
                          </td>

                          {/* Runway */}
                          <td>
                            {runwayMonths != null ? (
                              <span
                                className="tabular-nums text-sm font-medium"
                                style={{
                                  color: runwayMonths < 2
                                    ? 'var(--danger-text)'
                                    : runwayMonths < 4
                                      ? 'var(--warning-text)'
                                      : 'var(--success-text)',
                                }}
                              >
                                {runwayMonths} mo
                              </span>
                            ) : (
                              <span style={{ color: 'var(--text-tertiary)', fontSize: 13 }}>—</span>
                            )}
                          </td>

                          {/* Wallet status */}
                          <td>
                            <span className={w.is_active ? 'badge-success' : 'badge-neutral'}>
                              {w.is_active ? 'Active' : 'Inactive'}
                            </span>
                          </td>

                          {/* Last updated */}
                          <td style={{ color: 'var(--text-tertiary)', fontSize: 12 }}>
                            {fmtDate(w.updated_at)}
                          </td>

                          {/* Actions */}
                          <td>
                            {tenant && (
                              <div className="flex items-center gap-1">
                                <button
                                  onClick={() => setTopUpTenant(tenant)}
                                  className="btn-secondary"
                                  style={{ height: 28, fontSize: 12, padding: '0 10px' }}
                                >
                                  <Plus className="h-3 w-3" />
                                  Top Up
                                </button>
                                <button
                                  onClick={() => setTxnTenant(tenant)}
                                  className="btn-secondary"
                                  style={{ height: 28, fontSize: 12, padding: '0 10px' }}
                                  title="View transaction history"
                                >
                                  History
                                </button>
                              </div>
                            )}
                          </td>
                        </tr>
                      )
                    })
                )}
              </tbody>
            </table>
          </div>
        </div>
      ))}

      {/* ── Plans tab modals ──────────────────────────────────────────────── */}
      {assignTenant && (
        <AssignPlanModal
          tenant={assignTenant}
          services={services}
          pricingCatalog={pricingCatalog}
          onClose={() => setAssignTenant(null)}
          onDone={load}
        />
      )}
      {changePlanFor && (
        <ChangePlanModal
          subscription={changePlanFor.sub}
          tenantName={changePlanFor.tenant.name}
          pricingCatalog={pricingCatalog}
          coreServiceSlug={coreService?.slug ?? 'insurance_crm'}
          onClose={() => setChangePlanFor(null)}
          onDone={load}
        />
      )}
      {cancelFor && (
        <CancelConfirm
          tenantName={cancelFor.tenant.name}
          onConfirm={() => { handleLifecycle(cancelFor!.sub.id, 'cancel'); setCancelFor(null) }}
          onClose={() => setCancelFor(null)}
        />
      )}
      {addAddonTenant && (
        <AddAddonModal
          tenant={addAddonTenant}
          services={services}
          pricingCatalog={pricingCatalog}
          existingSubSlugs={
            (subsByTenant[addAddonTenant.id] ?? [])
              .filter(s => s.status?.toLowerCase() === 'active')
              .map(s => s.service_slug ?? '')
          }
          onClose={() => setAddAddonTenant(null)}
          onDone={load}
        />
      )}
      {removeAddon && (
        <RemoveAddonConfirm
          addonName={removeAddon.sub.service_name}
          tenantName={removeAddon.tenant.name}
          onConfirm={() => { handleRemoveAddon(removeAddon.sub.id); setRemoveAddon(null) }}
          onClose={() => setRemoveAddon(null)}
        />
      )}

      {/* ── Wallets tab modals ────────────────────────────────────────────── */}
      {topUpTenant && (
        <TopUpModal
          tenant={topUpTenant}
          onClose={() => setTopUpTenant(null)}
          onDone={load}
        />
      )}
      {txnTenant && (
        <TransactionDrawer
          tenant={txnTenant}
          onClose={() => setTxnTenant(null)}
        />
      )}
    </div>
  )
}
